import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Collapse, IconButton, makeStyles, Typography } from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ImageIcon from '@material-ui/icons/Image';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import {
  removeEditorItemAndSync,
  setEditorSelectedId,
  updateEditorImageFromUpload,
} from '../../Reducers/Configurator/Actions';
import {
  getEditorSelectedId,
  getOptionsForClassification,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import type { ImageItem } from '../../Reducers/Configurator/types';
import { XXL_COLORS } from '../../theme/colors';
import { VECTOR_UPLOAD_EXTENSIONS } from '../../Enums';
import { replaceSvgColorAreas } from '../../utils/svgColorReplace';
import {
  buildDisplaySvg,
  extractSvgInnerContent,
  getTrimViewBoxFromItem,
} from '../../utils/svgDocument';
import ComponentOptionsSection from './ComponentOptionsSection';
import ImageColorAreasSection from './ImageColorAreasSection';
import SizeSection from './SizeSection';
import { useItemColorConflict } from './useItemColorConflict';
import ErrorOutline from '@material-ui/icons/ErrorOutline';

function resolveUploadUrl(result: unknown): string | null {
  if (typeof result === 'string' && result) return result;
  if (result && typeof result === 'object') {
    const preview = (result as { preview?: { url?: string } }).preview;
    return preview?.url ?? null;
  }
  return null;
}

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    card: {
      border: `1px solid ${tokens.borderStrong}`,
      marginBottom: theme.spacing(1.25),
      overflow: 'hidden',
      backgroundColor: tokens.paperBackground,
      transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
    },
    cardOpen: {
      borderColor: tokens.accent,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      cursor: 'pointer',
      backgroundColor: tokens.paperBackground,
      userSelect: 'none',
      transition: 'background-color 0.12s ease',
      '&:hover': { backgroundColor: tokens.surfaceMuted },
    },
    headerOpen: {
      borderBottom: `1px solid ${tokens.borderLight}`,
      backgroundColor: tokens.selectedTint,
      '&:hover': { backgroundColor: tokens.selectedTint },
    },
    preview: {
      width: 24,
      height: 24,
      flexShrink: 0,
      marginRight: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: tokens.textBase,
    },
    summary: {
      flex: 1,
      minWidth: 0,
    },
    summaryText: {
      fontSize: 13,
      fontWeight: 600,
      color: tokens.sectionTitle,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.3,
    },
    summaryMeta: {
      fontSize: 11,
      color: tokens.textBase,
      marginTop: 2,
    },
    expandIcon: {
      flexShrink: 0,
      color: tokens.textBase,
      transition: 'transform 0.2s ease',
    },
    expandIconOpen: {
      transform: 'rotate(180deg)',
    },
    deleteButton: {
      flexShrink: 0,
      padding: 4,
      color: tokens.textBase,
      '&:hover': { color: theme.palette.error.main },
    },
    warningIcon: {
      flexShrink: 0,
      color: theme.palette.error.main,
    },
    body: {
      padding: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    dropZone: {
      border: `2px dashed ${tokens.borderStrong}`,
      padding: theme.spacing(2),
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: tokens.paperBackground,
      color: tokens.textBase,
      '&:hover': { borderColor: tokens.accent },
    },
    dropZoneUploading: {
      borderColor: tokens.tileSelectedBorder,
      backgroundColor: tokens.tileSelectedBg,
    },
  };
});

type Props = { item: ImageItem };

export default function ImageItemCard({ item }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const selectedId = useSelector(getEditorSelectedId);
  const surfaceOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.surface));

  const isOpen = item.id === selectedId;
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { hasConflict } = useItemColorConflict(item);

  const itemSurfaceId = item.selectedOptions?.[OptionClassificationIdentifiers.surface];
  const surfaceName = surfaceOptions.find((o) => o.identifier === itemSurfaceId)?.title ?? '';

  const coloredPreview = React.useMemo(() => {
    if (!item.svgSource?.trim()) return '';
    const displaySvg = buildDisplaySvg(item.svgSource, getTrimViewBoxFromItem(item));
    const colored = item.colorAreas?.length
      ? replaceSvgColorAreas(displaySvg, item.colorAreas)
      : displaySvg;
    return extractSvgInnerContent(colored);
  }, [
    item.svgSource,
    item.colorAreas,
    item.trimViewBoxX,
    item.trimViewBoxY,
    item.viewBoxWidth,
    item.viewBoxHeight,
  ]);

  const handleHeaderClick = () => {
    dispatch(setEditorSelectedId(isOpen ? null : item.id));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(removeEditorItemAndSync(item.id));
  };

  const validateFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext ? VECTOR_UPLOAD_EXTENSIONS.includes(ext) : false;
  };

  const processUpload = async (file: File) => {
    if (!validateFile(file)) return;
    setUploading(true);
    try {
      const result = await Services.designer.uploadFile(file);
      const url = resolveUploadUrl(result);
      if (!url) return;

      const preview = Services.store.state.imageGallery?.selectedImage?.preview;
      const imageData = Services.store.state.imageGallery?.selectedImage;
      await dispatch(
        updateEditorImageFromUpload(item.id, url, {
          preview: { width: preview?.width, height: preview?.height },
          imageData: imageData || undefined,
        })
      );
    } catch (err) {
      console.error('Upload failed', err);
      Services.ui?.reportError?.(t('imageItemCard.uploadError'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFile = (file: File) => {
    if (validateFile(file)) processUpload(file);
  };

  return (
    <div className={clsx(classes.card, isOpen && classes.cardOpen)}>
      <div
        className={clsx(classes.header, isOpen && classes.headerOpen)}
        onClick={handleHeaderClick}
      >
        <div className={classes.preview}>
          {coloredPreview ? (
            <svg
              width={24}
              height={24}
              viewBox={`${item.trimViewBoxX ?? 0} ${item.trimViewBoxY ?? 0} ${item.viewBoxWidth || 100} ${item.viewBoxHeight || 100}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <g dangerouslySetInnerHTML={{ __html: coloredPreview }} />
            </svg>
          ) : (
            <ImageIcon fontSize="small" />
          )}
        </div>
        <div className={classes.summary}>
          <div className={classes.summaryText}>{t('imageItemCard.title')}</div>
          {surfaceName && <div className={classes.summaryMeta}>{surfaceName}</div>}
        </div>
        {hasConflict && (
          <ErrorOutline
            className={classes.warningIcon}
            fontSize="small"
            aria-label={t('textItemCard.colorConflictBadgeLabel')}
          />
        )}
        <ExpandMore
          className={clsx(classes.expandIcon, isOpen && classes.expandIconOpen)}
          fontSize="small"
        />
        <IconButton className={classes.deleteButton} size="small" onClick={handleDelete} aria-label={t('common.delete')}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </div>

      <Collapse in={isOpen} timeout={200} unmountOnExit>
        <div className={classes.body}>
          <div
            className={clsx(classes.dropZone, uploading && classes.dropZoneUploading)}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file && !uploading) handleFile(file);
            }}
          >
            {uploading ? (
              <Typography variant="body2">{t('imageItemCard.uploading')}</Typography>
            ) : (
              <>
                <Typography variant="body2">
                  {item.url ? t('imageItemCard.dropToReplace') : t('imageItemCard.dropToUpload')}
                </Typography>
                <Typography variant="caption">{t('imageItemCard.formats')}</Typography>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".svg,.pdf,.eps,.ai"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
            />
          </div>
          <ComponentOptionsSection classificationId={OptionClassificationIdentifiers.surface} />
          <ImageColorAreasSection item={item} />
          <SizeSection />
        </div>
      </Collapse>
    </div>
  );
}
